import { type PopupType } from '@hooks-api/types/dataModel/popup'
import { Box } from '@pets-ui/Box/Box'
import { Image } from '@pets-ui/Image/Image'
import { Link } from '@pets-ui/Link/Link'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useResponsive } from '@pets-ui/useResponsive/useResponsive'
import { type ExtendStyleProps } from '@pets-ui/utils/makeExtendStyle/makeExtendStyle'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { type ComponentPropsWithoutRef } from 'react'
import PlaceholderImage from '../assets/placeholder-image-center.svg?react'

const pcSizeMap: Record<PopupType, { maxWidth?: string, maxHeight?: string, minWidth?: string, minHeight?: string }> = {
  center: {
    maxWidth: '600px',
    maxHeight: '600px',
    minWidth: '288px',
    minHeight: '288px',
  },
  bottom: {
    maxWidth: '600px',
    maxHeight: '360px',
    minWidth: '358px',
    minHeight: '214px',
  },
  floating: {
    maxWidth: '400px',
    maxHeight: '360px',
  },
}

const mobileSizeMap: Record<PopupType, { maxWidth?: string, maxHeight?: string, minWidth?: string, minHeight?: string }> = {
  center: {
    maxWidth: '288px',
    maxHeight: '288px',
    minWidth: '288px',
    minHeight: '288px',
  },
  bottom: {
    maxWidth: '358px',
    maxHeight: '214px',
    minWidth: '358px',
    minHeight: '214px',
  },
  floating: {
    maxWidth: '300px',
    maxHeight: '200px',
  },
}

const baseStyle = stylex.create({
  image: {
    display: 'block',
    objectFit: 'contain',
    maxHeight: '100%',
    width: '100%',
  },
})

const floatingImageStyles = stylex.create({
  img: (maxWidth?: string, maxHeight?: string) => ({
    height: 'auto',
    maxHeight,
    maxWidth,
    width: 'auto',
  }),
})

export interface PopupContentProps extends ExtendStyleProps, ComponentPropsWithoutRef<'div'> {
  content?: string
  image?: string
  popupType?: PopupType
  linkUrl?: string
  width?: number
  onClick?: () => void
  onPopupClickEvent?: () => void
  backgroundHidden?: boolean
}

export const PopupContent = (props: PopupContentProps) => {
  const { content, image, popupType = 'center', linkUrl, onClick, onPopupClickEvent, backgroundHidden } = props
  const isMobile = useResponsive('mobile')
  const imageSize = (isMobile ? mobileSizeMap : pcSizeMap)[popupType]

  const handleFloatingClick = () => {
    onClick?.()
    onPopupClickEvent?.()
  }

  return (
    <Box overflow="hidden">
      {content && (
        <Box mb={3}>
          <Text
            align="center"
            color={backgroundHidden ? colors.white : colors.grey800}
          >
            {content.split('\n').map((line, index) => (
              <span key={index}>
                {line}
                {index < content.split('\n').length - 1 && <br />}
              </span>
            ))}
          </Text>
        </Box>
      )}
      {image && (
        <VStack
          mb={2}
          mx="auto"
          minWidth={isMobile ? mobileSizeMap[popupType].minWidth : pcSizeMap[popupType].minWidth}
          minHeight={isMobile ? mobileSizeMap[popupType].minHeight : pcSizeMap[popupType].minHeight}
          maxWidth={isMobile ? mobileSizeMap[popupType].maxWidth : pcSizeMap[popupType].maxWidth}
          maxHeight={isMobile ? mobileSizeMap[popupType].maxHeight : pcSizeMap[popupType].maxHeight}
          alignItems="center"
          justifyContent="center"
          overflow={popupType === 'floating' ? 'visible' : 'hidden'}
        >
          <Link target="_blank" href={linkUrl} onClick={handleFloatingClick}>
            <Image
              src={image}
              fallback={<PlaceholderImage />}
              rootStyle={baseStyle.image}
              xStyles={popupType === 'floating' ? { imgBase: { img: floatingImageStyles.img(imageSize.maxWidth, imageSize.maxHeight) } } : undefined}
            />
          </Link>
        </VStack>
      )}
    </Box>
  )
}
